import {
  AfterViewInit,
  Component, ElementRef, EventEmitter, Input,
  OnDestroy,
  OnInit, QueryList,
  ViewChild, ViewChildren,
} from '@angular/core';
import {ActivatedRoute} from "@angular/router";
import {RequestService} from "../../core/services/request/request.service";
import {LegacyNavigateService} from "../../core/services/legacy-navigate/legacy-navigate.service";
import {fromEvent, Observable, Subscription, of} from "rxjs";
import {KnowledgeModel} from "./knowledge-model";
import {KnowledgeSearchResultsModel} from "./knowledge-search-results-model";
import {debounceTime, mergeMap} from "rxjs/operators";
import {HtmlEditorService} from '@syncfusion/ej2-angular-richtexteditor';
import {KnowledgeBaseContextModel} from "./knowledge-base-context-model";
import {PortalDataService} from "../../core/services/portal-data/portal-data.service";
import {UserService} from 'src/app/core/services/user/user.service';
import {InternationalizationService} from "../../core/services/internationalization/internationalization.service";
import {$translate, $translateKey} from "../../core/services/internationalization/localize.service";
import {
  AnimationSettings,
  ListViewComponent,
  SelectedCollection,
  VirtualizationService
} from "@syncfusion/ej2-angular-lists";
import {KnowledgeBaseModel} from "./knowledge-base-model";
import {KnowledgeBaseClassificationModel} from "./knowledge-base-classification-model";
import {KnowledgeBaseClassificationContextModel} from "./knowledge-base-classification-context-model";
import {KnowledgeTagModel} from "./knowledge-tag-model";
import {SelectEventArgs} from "@syncfusion/ej2-lists/src/list-view/list-view";
import {UtilService} from "../../core/services/util/util.service";
import {KnowledgeTagContextModel} from "./knowledge-tag-context-model";
import {
  BreadcrumbItemModel, NodeAnimationSettingsModel, TreeViewComponent
} from "@syncfusion/ej2-angular-navigations";
import {ClickEventArgs} from "@syncfusion/ej2-buttons/src/chips/chip-list";
import {NodeSelectEventArgs} from "@syncfusion/ej2-navigations/src/treeview/treeview";
import {FieldsSettingsModel as TreeViewFieldSettingsModel} from "@syncfusion/ej2-navigations/src/treeview/treeview-model";


enum KnowledgeLocation {
  Init,
  ClassificationView,
  TagView,
  SearchView,
  ArticleView,
  SingleArticleView,
  ArticleNotFoundView,
  SingleArticleNotFoundView
}

@Component({
  selector: 'sb-knowledge-page,sb-knowledge-article',
  templateUrl: './knowledge-page.component.html',
  styleUrls: ['./knowledge-page.component.scss'],
  providers: [
    HtmlEditorService,
    VirtualizationService,
  ],
})
export class KnowledgePageComponent implements OnInit, OnDestroy, AfterViewInit {

  KnowledgeLocation = KnowledgeLocation; // Needed because otherwise the template cannot see it.

  @Input("article-id")
  embeddedArticleId: string;

  location: KnowledgeLocation;

  // Knowledge article search

  activatedRoute$: Subscription | null;

  searchTitle = "";

  searchPlaceholder = "";

  searchText = "";

  @ViewChildren("fullTextSearch")
  fullTextSearch: QueryList<any>;

  fullTextSearch$: Subscription;

  singleResultTitle = "";

  notFoundTitle = "";
  notFoundMessage = "";
  notFoundButtonText = "";

  noResultsMessage = "";

  knowledgeBaseTitle = "";

  searchResults: KnowledgeSearchResultsModel;

  knowledgeBaseIds: string[] = [];

  knowledgeBases: KnowledgeBaseContextModel[] = [];

  query: any;

  classificationListLookup: { [key: string]: KnowledgeBaseClassificationContextModel } = {};

  classificationList: KnowledgeBaseClassificationContextModel[] = [];

  classificationListFields: TreeViewFieldSettingsModel = {
    id: "id",
    text: "text",
    iconCss: 'iconCss',
    child: 'child',
    dataSource: []
  };

  tagList: KnowledgeTagContextModel[] = [];

  tagListColors: { [key: string]: string } = {};

  currentClassificationId: string;

  classificationListAnimation: NodeAnimationSettingsModel = {
    expand: {effect: 'SlideDown', duration: 200, easing: 'linear'},
    collapse: {effect: 'SlideUp', duration: 200, easing: 'linear'}
  };

  classificationListTitle = "";

  @ViewChild("classificationListView")
  classificationListView: TreeViewComponent;

  tagListAnimation: AnimationSettings = {
    effect: "SlideLeft"
  };

  tagListTitle = "";

  currentTagIds: string[] = [];

  @ViewChild("tagListView")
  tagListView: ListViewComponent;

  knowledgeResultsInitialRequestSize = 4;
  knowledgeResultsScrollingRequestSize = 1;

  knowledgeResults: KnowledgeModel[] = undefined;

  totalResultCount = 0;

  @ViewChildren("knowledgeListResults")
  knowledgeListResults: QueryList<ElementRef>;

  knowledgeListResults$: Subscription;

  private debounce$: Subscription | null;

  private lastScrollFirstResult: number = -1;

  private scrollId = "#knowledgeListResults";

  // Knowledge article display

  articleId: string;

  article: KnowledgeModel;

  breadcrumbLocation: KnowledgeLocation;

  breadcrumb: BreadcrumbItemModel[];

  constructor(private activatedRoute: ActivatedRoute,
              private requestService: RequestService,
              private portalDataService: PortalDataService,
              private legacyNavigateService: LegacyNavigateService,
              private i18nService: InternationalizationService,
              private userService: UserService,
              private utilService: UtilService) {
  }

  async ngOnInit() {

    this.searchTitle = $translate("ng.component.knowledge-page.search-title")`Welcome to our knowledge base, ${this.userService.getFirstName()}`;
    this.searchPlaceholder = $translate("ng.component.knowledge-page.search-placeholder")`What are you looking for?`;
    this.singleResultTitle = $translate("ng.component.knowledge-page.single-result-title")`Knowledge article`;
    this.classificationListTitle = $translate("ng.component.knowledge-page.classification-list-title")`All topics`;
    this.tagListTitle = $translate("ng.component.knowledge-page.tag-list-title")`All categories`;
    this.notFoundTitle = $translate("ng.component.knowledge-page.not-found-title")`Oops, this article is missing`;
    this.notFoundMessage = $translate("ng.component.knowledge-page.not-found-message")`We searched high and low but couldn’t find what you’re looking for. Let’s find a better place for you to go.`;
    this.notFoundButtonText = $translate("ng.component.knowledge-page.go-to-knowledge-base")`Go to knowledge base`;
    this.noResultsMessage = $translate("ng.component.knowledge-page.no-results-message")`No results found.`;
    this.knowledgeBaseTitle = $translate("ng.component.knowledge-page.knowledge-base-title")`Knowledge base`;

    // Wipe out the page context
    (window as any).KO_PAGE_CONTEXT.clear();

    // Wipe out the Knockout content.
    const pageContentMain = document.getElementById("page-content-main");
    if (pageContentMain) {
      pageContentMain.innerHTML = "";
    }
    const errorConsole = document.getElementById("error-console");
    if (errorConsole) {
      errorConsole.innerHTML = "";
    }

    // Mark as portal page
    document.getElementById("detailMain")?.classList?.add("sb-portal-page-content");

    // Find the records to display, based on the URL.
    this.activatedRoute$ = this.activatedRoute.paramMap.subscribe(async params => {

      const queryParams = (this.activatedRoute.queryParams as any).value; // Cheating way to do it.

      if (queryParams) {
        const queryParam = queryParams['query'];
        this.query = queryParam ? JSON.parse(queryParam) : null;
      }

      if (this.embeddedArticleId) {
        await this.loadSingleArticle(this.embeddedArticleId);
      } else {
        // Check to see if we have an id supplied, if so we are displaying a single knowledge article.
        let id = params.get("id");
        if (id) {
          await this.loadSingleArticle(id);
        } else {
          if (![KnowledgeLocation.SingleArticleView, KnowledgeLocation.SingleArticleNotFoundView, KnowledgeLocation.ArticleNotFoundView].includes(this.location)) {

            this.location = KnowledgeLocation.ClassificationView;

            await this.populateKnowledgeBases();

            await this.populateClassifications();

            await this.populateTags();

            await this.performKnowledgeSearch(true);
          }
        }
      }
    });

    if (this.embeddedArticleId) {
      await this.loadSingleArticle(this.embeddedArticleId);
    }

  }

  ngAfterViewInit() {
    this.addScrollListener();
    this.knowledgeListResults$ = this.knowledgeListResults.changes.subscribe((change) => {
      if (!this.knowledgeListResults.last) {
        this.removeScrollListener();
      } else {
        this.addScrollListener();
      }
    });
  }

  ngOnDestroy() {
    this.activatedRoute$ && this.activatedRoute$.unsubscribe();
    this.debounce$ && this.debounce$.unsubscribe();
    this.fullTextSearch$ && this.fullTextSearch$.unsubscribe();
    this.knowledgeListResults$ && this.knowledgeListResults$.unsubscribe();

    this.removeScrollListener();
  }

  private addScrollListener() {
    document.querySelector(this.scrollId)?.addEventListener('scroll', this.knowledgeResultsOnScrolled);
  }

  private removeScrollListener(): void {
    document.querySelector(this.scrollId)?.removeEventListener('scroll', this.knowledgeResultsOnScrolled);
  }

  async loadSingleArticle(id: string) {
    try {
      const article = await this.getKnowledgeArticle(id, true);

      if (article) {
        this.article = article;
        this.knowledgeBaseIds = [this.article.KnowledgeBase.record.id];

        await this.populateClassifications();

        await this.populateTags();

        this.location = KnowledgeLocation.SingleArticleView;
      } else {
        this.location = KnowledgeLocation.SingleArticleNotFoundView;
      }
    } catch (e) {
      console.error(`KnowledgePageComponent: error loading article with id ${id}`);
      this.location = KnowledgeLocation.SingleArticleNotFoundView;
    }
  }

  /**
   * Relevant knowledge bases are ones that are listed in the users effective portal definition,
   * otherwise ones that they have permission to see.
   */
  async getRelevantKnowledgeBases(kbIds: string[]): Promise<KnowledgeBaseContextModel[]> {
    const data = {
      kbIds: kbIds
    };

    const result: any = await this.requestService.createRequest()
      .controller("servicely.core.Knowledge")
      .method("getRelevantKnowledgeBases")
      .data(data)
      .execute();

    const kbs = result.data as KnowledgeBaseModel[];

    return kbs.map(r => {
      return <KnowledgeBaseContextModel>{
        kb: r,
        selected: false,
        count: 0,
        totalCount: 0
      };
    });
  }

  /**
   * Relevant classifications are ones that are used on knowledge articles from relevant knowledge bases.
   * The articles do not need to be published.
   */
  async getRelevantClassifications(kbIds: string[]): Promise<KnowledgeBaseClassificationModel[]> {
    const data = {
      kbIds: kbIds,
      lookAtRecords: false,
    };

    const result: any = await this.requestService.createRequest()
      .controller("servicely.core.Knowledge")
      .method("getRelevantClassifications")
      .data(data)
      .execute();

    return result.data as KnowledgeBaseClassificationModel[];
  }

  /**
   * Relevant tags are ones that are listed on relevant knowledge bases.
   */
  async getRelevantTags(kbIds: string[]): Promise<KnowledgeTagModel[]> {
    const data = {
      kbIds: kbIds,
      lookAtRecords: false,
    };

    const result: any = await this.requestService.createRequest()
      .controller("servicely.core.Knowledge")
      .method("getRelevantTags")
      .data(data)
      .execute();

    return result.data as KnowledgeTagModel[];
  }

  async getKnowledgeArticle(id: string, singleOnly: boolean = false): Promise<KnowledgeModel> {
    const articles = await this.getKnowledgeArticles([], [], [], [id], null, null, 0, 1, singleOnly);

    return articles?.[0];
  }

  async getKnowledgeArticles(kbIds: string[],
                             classificationIds: string[],
                             tagIds: string[],
                             ids: string[],
                             searchText: string,
                             jsonQuery: any,
                             firstResult: number,
                             maxResults: number,
                             singleOnly: boolean = false): Promise<KnowledgeModel[]> {
    const queryParam = jsonQuery ? JSON.stringify(jsonQuery) : null;

    const data = {
      kbIds: kbIds,
      classificationIds: classificationIds,
      tagIds: tagIds,
      ids: ids,
      searchText: searchText,
      jsonQuery: queryParam,
      firstResult: firstResult,
      maxResults: maxResults,
      orderBy: 'Title'
    };

    const result: any = await this.requestService.createRequest()
      .controller("servicely.core.Knowledge")
      .method("getKnowledgeArticles")
      .data(data)
      .execute();

    if (!singleOnly) {
      const recordCount = result.data.totalCount;

      this.updateRecordCount(recordCount);

      const tagCounts = result.data.tagCounts;

      this.updateTagCounts(tagCounts);

      const classificationCounts = result.data.classificationCounts;

      this.updateClassificationCounts(classificationCounts);
    }

    return result.data.results as KnowledgeModel[];
  }

  async search() {
    this.location = KnowledgeLocation.ClassificationView;
    this.utilService.tickThen(async () => {
      await this.performKnowledgeSearch(true);
    });
  }

  clearSearch() {
    this.searchText = "";
    this.search();
  }

  // Knowledge base support

  toggleKnowledgeBaseFilter(e: Event) {
    const input = e.target as HTMLInputElement;
    const checked = input.checked;
    const id = input.id.split("-")[1];
    this.knowledgeBases.filter((kb) => kb.kb.id === id).forEach(kb => kb.selected = checked);

    this.search();
  }

  // Fake button navigation

  buttonLabel(key: string): string {
    return this.i18nService.getMessage(key);
  }

  patchClassificationListView() {
    if (this.classificationListView) {
      // Replace the setSelectLI method with one that can toggle the selected item.
      const existingToggleSelect = (this.classificationListView as any).toggleSelect; // Private method

      if (!existingToggleSelect.patched) {
        (this.classificationListView as any).toggleSelect = (li: any, e: any, multiSelect: boolean) => {
          if (li && li.classList.contains("e-active")) {
            (this.classificationListView as any).unselectNode(li, e); // Private method
            (li as HTMLElement).classList.remove("e-node-focus");
          } else {
            existingToggleSelect.bind(this.classificationListView)(li, e, multiSelect);
          }
        }
        // Mark as patched so we don't double patch
        (this.classificationListView as any).toggleSelect.patched = true;
      }
    }
  }

  knowledgeResultsOnScrolled = async () => {
    await this.performKnowledgeSearch(false);
  }

  async populateKnowledgeBases() {
    const definition = await this.portalDataService.getEffectiveDefinition();
    const explicitKnowledgeBaseIds = definition?.KnowledgeBases ? definition?.KnowledgeBases?.records.map(kb => kb.id) : [];
    this.knowledgeBases = await this.getRelevantKnowledgeBases(explicitKnowledgeBaseIds);
    this.knowledgeBaseIds = this.knowledgeBases.map(k => k.kb.id);
  }

  async populateClassifications() {
    const data = await this.getRelevantClassifications(this.knowledgeBaseIds);

    const contextData: KnowledgeBaseClassificationContextModel[] = data.map((m) => {
      return <KnowledgeBaseClassificationContextModel>{
        record: m,
        text: $translateKey(m.Name.value as string),
        id: m.id,
        child: [],
        count: 0
      };
    });

    this.classificationListLookup = contextData.reduce((acc, curr) => {
      acc[curr.id] = curr;
      return acc;
    }, {});

    contextData.forEach((m) => {
      const parent = this.classificationListLookup[m.record?.Parent?.record?.id];
      if (parent) {
        parent.child.push(m);
        parent.child.sort((a, b) => a.text.localeCompare(b.text));
      }
    });

    this.classificationList = contextData.filter(m => m.record.Parent?.record?.id == null).sort((a, b) => a.text.localeCompare(b.text));

    this.classificationListFields = {
      ...this.classificationListFields,
      ...{
        dataSource: this.classificationList as {[key: string]: any}[]
      }
    };
  }

  async populateTags() {
    const data = await this.getRelevantTags(this.knowledgeBaseIds);

    const indices = [...Array(20).keys()].map(x => ++x).sort((a, b) => 0.5 - Math.random());

    const contextData: KnowledgeTagContextModel[] = data.map((m, idx) => {
      return <KnowledgeTagContextModel>{
        record: m,
        text: $translateKey(m.Name.value as string),
        id: m.id,
        colorIndex: indices[idx % 20] + ""
      };
    });

    this.tagList = contextData.sort((a, b) => a.text.localeCompare(b.text));

    this.tagListColors = this.tagList.reduce((acc, curr, idx) => {
      acc[curr.id] = curr.colorIndex;
      return acc;
    }, {});
  }

  async performKnowledgeSearch(initial: boolean) {

    if (initial) {
      this.resetKnowledgeSearch();
    }

    if (!this.knowledgeListResults?.length) {
      return;
    }
    let complete = false;
    const div = document.querySelector(this.scrollId) as HTMLElement;
    while (!complete) {
      const resultCount = await this.performKnowledgeSearchOnce(initial);
      if (resultCount === 0) {
        complete = true;
      }
      const isScrollbarNearBottom = div.scrollHeight - div.scrollTop - div.clientHeight < 20;
      if (!isScrollbarNearBottom) {
        complete = true;
      }
    }
  }

  async performKnowledgeSearchOnce(initial: boolean): Promise<number> {
    const firstResult = this.knowledgeResults?.length || 0;

    // If scrolling, compare with last call and ignore if the same firstResult.
    // TODO: should be using rxjs for this...
    if (initial == false) {
      if (this.lastScrollFirstResult >= 0 && firstResult == this.lastScrollFirstResult) {
        // ignore search as duplicate of previous one
        return 0;
      } else {
        this.lastScrollFirstResult = firstResult;
      }
    } else {
      this.lastScrollFirstResult = -1;
    }

    const maxResults = initial ? this.knowledgeResultsInitialRequestSize : this.knowledgeResultsScrollingRequestSize;
    const classificationIds = this.generateClassificationSearchIds(this.currentClassificationId);
    const tagIds = this.currentTagIds;
    const ids = [];
    const searchText = this.searchText;
    const jsonQuery = this.query;
    const data = await this.getKnowledgeArticles(this.knowledgeBaseIds, classificationIds, tagIds, ids, searchText, jsonQuery, firstResult, maxResults);
    this.knowledgeResults = [...(this.knowledgeResults || []), ...data];

    return data.length;
  }

  resetKnowledgeSearch() {
    this.knowledgeResults = undefined;
  }

  generateClassificationSearchIds(classificationId: string): string[] {
    if (classificationId) {
      const root = this.classificationListLookup[classificationId];
      const subtree = this.collectClassificationSubtree(root);
      return subtree.map(d => d.id);
    } else {
      return [];
    }
  }

  collectClassificationSubtree(node: KnowledgeBaseClassificationContextModel, collector: KnowledgeBaseClassificationContextModel[] = []): KnowledgeBaseClassificationContextModel[] {
    node.child.forEach(d => this.collectClassificationSubtree(d, collector));
    collector.push(node);
    return collector;
  }

  populateBreadcrumb(location: KnowledgeLocation, article: KnowledgeModel) {
    this.breadcrumbLocation = location;

    let classification = this.classificationListLookup[article.Classification.value as string];

    const breadcrumb = [];

    while(classification) {
      breadcrumb.push({
        text: classification.text,
        url: "#",
        id: classification.id,
      });
      classification = this.classificationListLookup[classification.record.Parent?.record?.id];
    }
    breadcrumb.push(
      {
        text: 'Knowledge base',
        iconCss: 'chevron_left',
        url: "#",
      }
    );

    this.breadcrumb = breadcrumb.reverse();
  }

  tagColorIndex(id: string): string {
    return this.tagListColors[id];
  }

  updateRecordCount(count: number) {
    this.totalResultCount = count;
  }

  updateClassificationCounts(counts: { [key: string]: number }) {
    Object.values(this.classificationListLookup).forEach((c) => {
      c.count = 0;
    });

    // Update this.classificationList with records on the classification and their parents
    Object.entries(counts).forEach((k) => {
      const classId = k[0];
      const classRecord = this.classificationListLookup[classId];
      const collectedClassRecords = [classRecord];
      let parent = this.classificationListLookup[classRecord.record?.Parent?.record?.id];
      while (parent) {
        collectedClassRecords.push(parent);
        parent = this.classificationListLookup[parent.record?.Parent?.record?.id]
      }
      collectedClassRecords.forEach((c) => c.count = c.count + k[1]);
    });


    this.classificationListView.allowEditing = true;
    Object.values(this.classificationListLookup).forEach((c) => {
      this.classificationListView.updateNode(c.id, c.text + "|" + c.count);
    });
    this.classificationListView.allowEditing = false;
  }

  updateTagCounts(counts: { [key: string]: number }) {
    this.tagList.forEach(t => t.count = 0);

    Object.entries(counts).forEach((c) => {
      const tag = this.tagList.find(t => t.id == c[0]);
      if (tag) {
        tag.count = c[1];
      }
    });

    if (this.tagListView) {
      this.tagListView.dataBind();
    }
  }

  onClassificationTreeCreated($event: any) {
    this.patchClassificationListView();

    if (this.currentClassificationId) {
      this.classificationListView.selectedNodes = [this.currentClassificationId];
      this.classificationListView.ensureVisible(this.currentClassificationId);
    }
  }

  onClassificationNodeSelected($event: NodeSelectEventArgs) {
    this.utilService.tickThen(async () => {
      if ($event.action == 'select') {
        this.currentClassificationId = $event.nodeData['id'] as string;
      } else {
        this.currentClassificationId = null;
      }
      await this.performKnowledgeSearch(true);
    });
  }

  async onClassificationHeaderClicked($event: MouseEvent) {
    this.utilService.tickThen(async () => {
      this.currentClassificationId = null;
      this.classificationListView.selectedNodes = [];
      await this.performKnowledgeSearch(true);
    });
  }

  async onTagSelect($event: SelectEventArgs) {
    this.utilService.tickThen(async () => {
      this.currentTagIds = ((this.tagListView.getSelectedItems() as SelectedCollection).data as any[]).map(d => d.id);
      await this.performKnowledgeSearch(true);
    });
  }

  async onCardClick($event: MouseEvent, data: KnowledgeModel) {
    this.getKnowledgeArticle(data.id).then((article) => {
      this.populateBreadcrumb(this.location, article);
      this.location = KnowledgeLocation.ArticleView;
      this.articleId = data.id;
      this.article = article;
    }).catch((error) => {
      this.location = KnowledgeLocation.ArticleNotFoundView;
    });
  }

  onBreadcrumbItemClick($event: MouseEvent, index: number) {
    $event.preventDefault();

    this.location = this.breadcrumbLocation;
    this.articleId = null;
    this.article = null;

    this.currentClassificationId = index > 0 ? (this.breadcrumb[index] as any).id : null;

    if (this.currentClassificationId == null) {
      this.onClassificationHeaderClicked(null);
    }
  }

  onArticleNotFound($event: MouseEvent) {
    this.location = KnowledgeLocation.ClassificationView;
  }

  onTagChipClick($event: ClickEventArgs) {
  }

  onSearchButtonCreated($event: any) {
    this.bindSearchEvents();
  }

  private bindSearchEvents() {
    const searchInput = document.getElementById('fullTextSearch');
    if (searchInput) {
      this.debounce$ = fromEvent(searchInput, 'keyup').pipe(
        debounceTime(300)
      ).subscribe(data => this.search());

      if (this.fullTextSearch) {
        this.fullTextSearch$ = this.fullTextSearch.changes.subscribe((change) => {
          this.debounce$ && this.debounce$.unsubscribe();
          this.debounce$ = fromEvent(searchInput, 'keyup').pipe(
            debounceTime(300)
          ).subscribe(data => this.search());
        });
      }
    }
  }
}

